/**
 * PATH: packages/ui/src/kanban/components/board-view/BoardDockCustomizePanel.tsx
 * FILE_ID: UI-KANBAN-DOCK-CUSTOMIZE-001
 * PURPOSE: The dock customisation checklist — per-item show/hide toggles +
 *   the "Classic bottom bar" switch — as ONE shared panel, plus the static
 *   catalog of every dock item's label/icon.
 * WHY: FEAT-2026-07-03-KANBAN-FLOATING-DOCK follow-up (operator: "yess add
 *   this there too") — the same customise UI must render in BOTH the dock's
 *   ⋮ menu and the board-settings kebab (BoardHeaderMenu). One component,
 *   two mounts, zero forked checklists (rule 12: reuse).
 * FLOW:
 *   ┌──────────────┐   ┌────────────────────────┐   ┌─────────────────┐
 *   │ prefs +      │ → │ checklist rows +        │ → │ writes dockPrefs│
 *   │ item list    │   │ classic-bar toggle      │   │ via setPrefs    │
 *   └──────────────┘   └────────────────────────┘   └─────────────────┘
 * DEPENDENCIES:
 *   - lucide-react: item icons (mirrors BoardDock's mapping).
 *   - ./dockPrefs: KanbanDockPrefs + toggleDockItem + DockItemId.
 * RELATED:
 *   - ./BoardDockMenu.tsx: mounts this with the LIVE item list.
 *   - ./BoardHeaderMenu.tsx: mounts this with the full catalog.
 */
import {
  Calendar,
  CalendarDays,
  CalendarRange,
  Check,
  Coins,
  Filter as FilterIcon,
  GanttChartSquare,
  Inbox,
  LayoutGrid,
  LayoutList,
  Mail,
  NotebookPen,
  PanelBottom,
  PieChart,
  Table,
  Users as UsersIcon,
  Workflow,
  type LucideIcon,
} from 'lucide-react'

import { toggleDockItem, DOCK_ITEM_IDS, type DockItemId, type KanbanDockPrefs } from './dockPrefs'

export interface DockCustomizeItem {
  id: DockItemId
  label: string
  Icon: LucideIcon
}

/** Static catalog — the settings surface lists EVERY item (even ones the
 *  current host hasn't wired, e.g. Planner) so a pref set here is ready
 *  the moment the launcher becomes available. Labels/icons mirror
 *  BoardDock's live model. */
export const DOCK_ITEM_CATALOG: readonly DockCustomizeItem[] = [
  { id: 'mycards', label: 'My Cards', Icon: Inbox },
  { id: 'planner', label: 'Planner', Icon: CalendarDays },
  { id: 'notebooks', label: 'Notebooks', Icon: NotebookPen },
  { id: 'filters', label: 'Filters', Icon: FilterIcon },
  { id: 'emails', label: 'Emails', Icon: Mail },
  { id: 'board', label: 'Board', Icon: LayoutGrid },
  { id: 'gantt', label: 'Time view', Icon: GanttChartSquare },
  { id: 'smarttime', label: 'Smart Time', Icon: CalendarRange },
  { id: 'timeline', label: 'Timeline', Icon: Workflow },
  { id: 'swimlanes', label: 'Swimlanes', Icon: UsersIcon },
  { id: 'table', label: 'Table', Icon: Table },
  { id: 'calendar', label: 'Calendar', Icon: Calendar },
  { id: 'cashflow', label: 'Cash Flow', Icon: Coins },
  { id: 'dashboard', label: 'Dashboard', Icon: PieChart },
  { id: 'switch', label: 'Switch boards', Icon: LayoutList },
]

// Compile-time drift guard: every DockItemId appears in the catalog.
// (A missing row would silently drop the item from the settings surface.)
type CatalogIds = (typeof DOCK_ITEM_CATALOG)[number]['id']
const _exhaustive: readonly CatalogIds[] = DOCK_ITEM_IDS
void _exhaustive

const ROW =
  'flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs text-neutral-700 transition-colors hover:bg-neutral-100 dark:text-zinc-200 dark:hover:bg-zinc-900'

export interface BoardDockCustomizePanelProps {
  prefs: KanbanDockPrefs
  setPrefs: (next: KanbanDockPrefs) => void
  /** Live item list from the dock; defaults to the full catalog (settings). */
  items?: readonly DockCustomizeItem[]
  /** When provided (the dock's ⋮ mount), clicking a row's NAME navigates —
   *  runs that item — while the trailing checkbox still toggles show/hide
   *  (operator 2026-07-06: "I should be able to use the ⋮ to navigate to the
   *  docks which are not in my panel"). The settings mount (static catalog,
   *  nothing to run) omits it and rows stay toggle-only. */
  onNavigate?: (id: DockItemId) => void
}

export function BoardDockCustomizePanel({
  prefs,
  setPrefs,
  items = DOCK_ITEM_CATALOG,
  onNavigate,
}: BoardDockCustomizePanelProps) {
  return (
    <>
      <div className="max-h-48 overflow-y-auto" data-dock-customize="">
        {items.map((item) => {
          const Icon = item.Icon
          const shown = !prefs.hidden.includes(item.id)
          const checkbox = (
            <span
              className={[
                'grid h-4 w-4 place-items-center rounded border transition-colors',
                shown
                  ? 'border-teal-600 bg-teal-600 text-white'
                  : 'border-neutral-300 dark:border-zinc-700',
              ].join(' ')}
            >
              {shown && <Check className="h-3 w-3" />}
            </span>
          )
          if (!onNavigate) {
            // Settings mount — the whole row is one show/hide toggle.
            return (
              <button
                key={item.id}
                role="menuitemcheckbox"
                aria-checked={shown}
                type="button"
                data-testid={`settings-dock-${item.id}`}
                onClick={() => setPrefs(toggleDockItem(prefs, item.id))}
                className={ROW}
              >
                <Icon className="h-3.5 w-3.5" />
                <span className="flex-1">{item.label}</span>
                {checkbox}
              </button>
            )
          }
          // Dock ⋮ mount — the row NAME navigates (so every view/launcher is
          // reachable from the menu even when its icon is hidden or scrolled
          // off the strip); only the trailing checkbox toggles show/hide.
          // Two sibling buttons, never nested (invalid HTML + broken a11y).
          return (
            <div key={item.id} className="flex w-full items-stretch" data-dock-nav-row={item.id}>
              <button
                role="menuitem"
                type="button"
                onClick={() => onNavigate(item.id)}
                className={`${ROW} min-w-0 flex-1`}
                title={`Go to ${item.label}`}
              >
                <Icon className="h-3.5 w-3.5" />
                <span className="flex-1 truncate">{item.label}</span>
              </button>
              <button
                role="menuitemcheckbox"
                aria-checked={shown}
                aria-label={`${shown ? 'Hide' : 'Show'} ${item.label} on the dock`}
                type="button"
                onClick={() => setPrefs(toggleDockItem(prefs, item.id))}
                className="grid shrink-0 place-items-center px-3 transition-colors hover:bg-neutral-100 dark:hover:bg-zinc-900"
                title={shown ? 'Shown on the dock — click to hide' : 'Hidden from the dock — click to show'}
              >
                {checkbox}
              </button>
            </div>
          )
        })}
      </div>
      <div className="my-1 h-px bg-neutral-100 dark:bg-zinc-800" />
      {/* Escape hatch — bring back the legacy full-width strip. */}
      <button
        role="menuitemcheckbox"
        aria-checked={prefs.classicBar}
        type="button"
        data-testid="settings-dock-classic"
        onClick={() => setPrefs({ ...prefs, classicBar: !prefs.classicBar })}
        className={ROW}
        title="Use the classic full-width bottom bar instead of the floating dock"
      >
        <PanelBottom className="h-3.5 w-3.5" />
        <span className="flex-1">Classic bottom bar</span>
        <span className="text-[10px] uppercase tracking-wide text-neutral-400">
          {prefs.classicBar ? 'On' : 'Off'}
        </span>
      </button>
    </>
  )
}
